import React, { Fragment, useCallback, useMemo, useEffect } from 'react';
import get from 'lodash/get';
import { withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import SensorGrid from './SensorGrid';
import SensorList from './SensorList';
import { sensorsDistribution, subscription, subscriptionAllSensors } from '../../../graph/sensors';
import SensorAlerts from './SensorAlerts';
import styled from 'styled-components';
import { useSubscription } from '@apollo/client';
import { compose } from 'recompose';
import withFilterQueryString from '@/se/hocs/withFilterQueryString';
import DateFilter from '../analytics/DateFilter';
import SensorGlobalConfig from './SensorGlobalConfig';
import responsive from '@/se/utilities/responsive';
import TabNavigation from './TabNavigation';
import PrintDialog from './PrintDialog';
import { NamedRange } from '../../core/DatePicker';
import TitleAction from '@/se/components/TitleAction';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { withSession } from '../../../state/Session';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import { withScope } from '../../../contexts/ScopeContext';
export const Header = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  > span {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    max-height: 2.65625rem;
    ${responsive.md.andLarger`
      > * + * {
        margin-left: 0.5rem;
      }
    `}
  }

  ${responsive.md.andSmaller`
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr;
    margin-top: 1.5em;
    > span {
      margin-bottom: 3em;
      > * {
        width: 100%;
      }
      > * + * {
        margin-top: 0.5rem;
      }
    }
  `}
`;

const tabs = match => [
  {
    id: 'chart',
    name: 'Chart View',
    // icon: 'insert_chart_outlined',
    pathname: match.url,
  },
  {
    id: 'list',
    name: 'List View',
    // icon: 'toc',
    pathname: `${match.url}/list`,
  },
  {
    id: 'alert',
    name: 'Alerts',
    // icon: 'notifications_none',
    pathname: `${match.url}/alert`,
  },
];
const Title = () => <Typography variant="h2">Temp & Humidity</Typography>;

const SensorPage = ({ filter, setFilter, scope, location, match, isSuperAdmin }) => {
  // Cleanup: Remove old 'selected-sensors' localStorage value (feature removed)
  useEffect(() => {
    try {
      localStorage.removeItem('selected-sensors');
    } catch {
      // Ignore errors (e.g., private browsing mode)
    }
  }, []);

  const sensorSubscription = isSuperAdmin ? subscriptionAllSensors : subscription;
  const { data, loading } = useSubscription(sensorSubscription);
  const alertData = useSubscription(subscription);
  const sensors = isSuperAdmin ? get(data, 'sensorsAll', []) : get(data, 'sensors', []);
  const alertSensors = get(alertData?.data, 'sensors', []);

  // Get all sensor IDs for queries
  const sensorIds = useMemo(() => sensors.map(s => s.id), [sensors]);
  const sensorIdsAlert = useMemo(() => alertSensors.map(s => s.id), [alertSensors]);

  const isAlertPage = new RegExp('sensors\\/alert\\/\\d+$', 'gi').test(location.pathname);

  const now = useMemo(() => new Date(), []);

  const onDateRangeFilterChange = useCallback(val => setFilter({ dateRange: val }), [setFilter]);

  return (
    <Fragment>
      <TitleAction Title={Title}>
        {!isAlertPage && (
          <Grid container spacing={1}>
            <Grid item>
              <DateFilter value={get(filter, 'dateRange')} onChange={onDateRangeFilterChange} now={now} />
            </Grid>
          </Grid>
        )}
      </TitleAction>
      <Box display="flex" justifyContent="space-between" flexWrap="wrap" flexShrink={0}>
        <TabNavigation tabs={tabs(match)} location={location} showName />
        {!isAlertPage && (
          <Box display="flex" alignItems="center" mt={2}>
            <PrintDialog scope={scope} sensorIds={sensorIds} filter={filter} />
            <SensorGlobalConfig
              refetch={{
                query: sensorsDistribution,
                variables: { dateRange: filter.dateRange.toJSON(), sensors: sensorIds },
              }}
            />
          </Box>
        )}
      </Box>
      <Switch>
        <Route
          path={`${match.path}/alert`}
          render={() => (
            <SensorAlerts sensorIds={sensorIdsAlert} parentFilter={{ dateRange: filter.dateRange.toJSON() }} />
          )}
        />
        <Route path={`${match.path}/list`} render={() => <SensorList isSuperAdmin={isSuperAdmin} />} />
        <Route
          exact
          path={`${match.path}/`}
          render={() => <SensorGrid sensors={sensors} loading={loading} sensorIds={sensorIds} filter={filter} />}
        />
      </Switch>
    </Fragment>
  );
};

export default compose(
  withFilterQueryString({
    dateRange: NamedRange.lastSevenDays(),
  }),
  withRouter,
  withScope,
  withSession(unpackSessionObject)
)(SensorPage);
